// ItemPicker — the ONE way to look up a catalog item, anywhere.
//
// WHY THIS EXISTS
// Purchase-order lines, the receive drawer, scan-to-stock, quote review, import
// review and the adjust modal had each grown a private typeahead over
// GET /parts-catalog. Six lanes, six debounce timers, six ideas of what a match
// looks like, and only one of them told a screen reader anything. Worse, all six
// showed the SKU as the option's headline, which is the thing people in the shop
// say they cannot keep track of.
//
// So: one picker, name first, SKU demoted to the second line where it belongs.
// Type anything the server matches on (name, kind, SKU, MPN, description) and
// the item drops down.
//
// ── IT IS AN EntityCombobox, NOT A SECOND ONE ───────────────────────────────
// Keyboard, aria wiring, the mousedown-before-blur commit and the 120ms blur
// timeout all live there and none of it is reimplemented here. Three additions
// were needed to make that component work against a SERVER search rather than a
// known set, and all three are default-off there:
//
//   onQueryChange   the query is the combobox's own state; without it this
//                   component has nothing to search with
//   filterOptions   false, because the server already filtered. The client pass
//                   matches label/sublabel/chip only, so it would drop the row
//                   that matched on a description the option never prints
//   pinned          the create row survives the filter and sits last
//
// ── THE CREATE ROW IS A ROW ─────────────────────────────────────────────────
// Per the standing rule in web/CLAUDE.md, a picker whose value does not exist
// yet offers to create it in place. It carries the typed text in its label
// ("New item: “M3x8 screw”…") so nobody has to retype what they just typed, and
// `onCreate` hands that same text to the caller. Its sentinel id contains
// characters no catalog id can: ids are integers.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApi } from '../apiHelpers';
import EntityCombobox, { type ComboboxOption } from './EntityCombobox';
import useItemKindsStore, { kindByKey } from '../stores/useItemKindsStore';
import type { PartsCatalogRow } from '../Receiving/types';

/** The create row's id. Catalog ids are integers, so nothing can collide.
 *  Declared per lane by convention; this is the item lane's. */
export const CREATE_ITEM_OPTION_ID = '__create_new_item__';

/** Below this, a search is noise: two characters over a catalog of thousands
 *  returns most of it, and the round trip fires on every keystroke of a SKU. */
const MIN_QUERY = 2;

/** An item's headline in this picker. One definition, because the resting
 *  field shows it AND the "did they type over the selection" check compares
 *  against it. */
const optionLabel = (row: PartsCatalogRow): string =>
  row.base_name || row.display_name || row.sku;

/** Long enough that a typed word is one request, short enough that the list
 *  feels like it is following the typing. Same figure as the catalog page's
 *  own search box. */
const DEBOUNCE_MS = 250;

export interface ItemPickerProps {
  value: PartsCatalogRow | null;
  onSelect: (row: PartsCatalogRow | null) => void;
  /** When given, the list ends with a pinned "New item: “{query}”…" row;
   *  selecting it calls this with the typed text. */
  onCreate?: (query: string) => void;
  placeholder?: string;
  label?: string;
  id?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  excludeIds?: number[];
  pageSize?: number; // default 10
  className?: string;
  inputClassName?: string;
  /** Extra query params, e.g. { active: 'true' } */
  extraParams?: Record<string, string>;
}

const ItemPicker: React.FC<ItemPickerProps> = ({
  value,
  onSelect,
  onCreate,
  placeholder = 'Search by name, SKU or MPN',
  label,
  id,
  autoFocus = false,
  disabled = false,
  excludeIds,
  pageSize = 10,
  className,
  inputClassName,
  extraParams,
}) => {
  const api = useApi();

  const [query, setQuery] = useState('');
  const [rows, setRows] = useState<PartsCatalogRow[]>([]);
  /**
   * The query `rows` actually answers, or null while nothing does.
   *
   * WITHOUT THIS, ENTER LIES. The list is a server search: between a keystroke
   * and the response there is a window in which `rows` holds the answer to the
   * PREVIOUS query and the create row names the CURRENT one. Enter in that
   * window commits whatever is highlighted, which is a stale item or a mint of
   * a name whose match was still in flight. Gating both on "this answer is for
   * what is in the box" closes it; the 250ms debounce is untouched.
   */
  const [searchedFor, setSearchedFor] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);
  const [failed, setFailed] = useState(false);

  // The create row's subject, read at SELECT time. onSelect fires before the
  // combobox clears its query, so the state above is still the typed text when
  // the handler runs — but a ref cannot be wrong about it either way.
  const queryRef = useRef('');

  // The kind label on an option's chip. Cached for the session by the store, so
  // a picker mounted per PO line does not mean a fetch per PO line.
  const kinds = useItemKindsStore((s) => s.kinds);
  const loadKinds = useItemKindsStore((s) => s.load);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadKinds(api); }, []);

  // Object and array props are fresh identities every render, so they are
  // flattened to strings for the dependency lists. The effect reads the params
  // back out of its own key, which keeps the deps honest instead of lying about
  // what it closes over.
  const extraKey = JSON.stringify(extraParams || {});
  const excludeKey = (excludeIds || []).join(',');

  useEffect(() => {
    const q = query.trim();
    // The old answer stops being an answer the moment the question changes.
    // Clearing here rather than on arrival is what keeps a stale row from
    // sitting under the highlight during the debounce.
    setRows([]);
    setSearchedFor(null);
    if (q.length < MIN_QUERY) {
      setSearching(false);
      setFailed(false);
      return;
    }
    let stale = false;
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ q, page_size: String(pageSize) });
        Object.entries(JSON.parse(extraKey) as Record<string, string>).forEach(([k, v]) =>
          params.set(k, v)
        );
        const data = (await api.get(`/parts-catalog?${params.toString()}`)) as {
          rows?: PartsCatalogRow[];
        };
        if (stale) return;
        setRows(data?.rows || []);
        setSearchedFor(q);
        setFailed(false);
      } catch {
        // Silent by design: a search that failed is not a page-level failure,
        // and a toast per keystroke is how a picker becomes unusable on a bad
        // connection. The list says so instead.
        if (!stale) {
          setRows([]);
          // Deliberately NOT setSearchedFor(q): a failed search has not ruled
          // anything out, so the create row stays away. "Search failed, try
          // again" is the instruction, and minting past an unanswered lookup is
          // exactly how a duplicate gets in.
          setFailed(true);
        }
      } finally {
        if (!stale) setSearching(false);
      }
    }, DEBOUNCE_MS);
    // Ignoring the stale response rather than aborting the request: the flag is
    // per effect run, so an answer to a query nobody is typing any more can
    // never overwrite the answer to the one they are.
    return () => {
      stale = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, pageSize, extraKey]);

  const toOption = useCallback(
    (row: PartsCatalogRow): ComboboxOption => {
      const kind = row.kind_key ? kindByKey(kinds, row.kind_key) : undefined;
      return {
        id: String(row.id),
        // Name first. The SKU only leads on a row that has no name yet.
        label: optionLabel(row),
        // The identifiers people paste from a distributor or read off a box,
        // plus the variant when there is more than one of this thing.
        // `Retired` rides along only where a caller asked for retired rows
        // (include_inactive): the default list never carries one.
        sublabel:
          [row.sku, row.mpn, row.variant_detail, row.is_active === false ? 'Retired' : null]
            .filter(Boolean).join(' · ') || null,
        chip: kind?.label || null,
        data: row,
      };
    },
    [kinds]
  );

  const options = useMemo(() => {
    const list: ComboboxOption[] = [];
    const seen = new Set<string>();
    // The current value first and always: the combobox reads the field's
    // resting label off the OPTIONS, so a selection missing from the list
    // would leave the box blank over an item that is genuinely chosen.
    if (value) {
      list.push(toOption(value));
      seen.add(String(value.id));
    }
    const excluded = new Set(excludeKey ? excludeKey.split(',') : []);
    rows.forEach((row) => {
      const key = String(row.id);
      if (seen.has(key) || excluded.has(key)) return;
      seen.add(key);
      list.push(toOption(row));
    });
    const q = query.trim();
    // Only once the server has answered THIS query. See `searchedFor`.
    if (onCreate && q.length >= MIN_QUERY && !searching && searchedFor === q) {
      list.push({
        id: CREATE_ITEM_OPTION_ID,
        // Names its subject, per the action-row convention: "Create Acme Corp
        // as new vendor", not "Create as new vendor".
        label: `New item: “${q}”…`,
        chip: 'new',
        pinned: true,
      });
    }
    return list;
    // toOption changes only with the kinds list; excludeKey/extraKey are the
    // flattened forms of the array/object props.
  }, [value, rows, excludeKey, query, searchedFor, searching, onCreate, toOption]);

  const handleSelect = (option: ComboboxOption | null) => {
    if (!option) {
      onSelect(null);
      return;
    }
    if (option.id === CREATE_ITEM_OPTION_ID) {
      if (onCreate) onCreate(queryRef.current.trim());
      return;
    }
    onSelect((option.data as PartsCatalogRow) || null);
  };

  // Every state the list can be in has a sentence, and each one says what to do
  // next rather than only what happened.
  const message = failed
    ? 'Search failed, try again'
    : searching
      ? 'Searching…'
      : query.trim().length < MIN_QUERY
        ? 'Type at least two characters: a name, a SKU, an MPN.'
        : 'No item matches that.';

  return (
    <EntityCombobox
      id={id}
      label={label}
      placeholder={placeholder}
      value={value ? String(value.id) : ''}
      options={options}
      onSelect={handleSelect}
      onQueryChange={(q) => {
        queryRef.current = q;
        setQuery(q);
        // TYPING OVER A CHOSEN ITEM UN-CHOOSES IT. EntityCombobox never calls
        // onSelect(null) on its own — it has no clear button — so without this
        // the caller keeps holding the previous row while the box shows
        // something else, and a form that gates on "an item is selected" stages
        // the wrong one.
        //
        // Only on a NON-EMPTY query that differs from the selection's own
        // label: '' is how the component reports Escape, a commit and the blur
        // timeout, and at rest the field falls back to showing the selection.
        if (value && q && q !== optionLabel(value)) onSelect(null);
      }}
      // The server matched on name, kind, SKU, MPN and description; a second
      // pass over label and sublabel alone would throw half of that away.
      filterOptions={false}
      disabled={disabled}
      autoFocus={autoFocus}
      // A field focused at mount would otherwise land with its list open over
      // whatever sits below it, before anybody has asked for anything.
      openOnFocus={!autoFocus}
      className={className}
      inputClassName={inputClassName}
      emptyMessage={message}
      noOptionsMessage={message}
    />
  );
};

export default ItemPicker;
